/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { Modal, theme, Message } from "@nordcloud/gnui";
import { NextPatchingActionForResource } from "~/generated/graphql";
import { ModalContent } from "~/components/Styled";
import { TableWrapper } from "~/components/Tables";
import { FlexColumn } from "~/components/Tables/styles";
import { dateFormat } from "~/constants/dateFormats";
import { ROUTES } from "~/routing/routes";
import { NextPatchingActionTooltip } from "./NextPatchingActionTooltip";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  actions: NextPatchingActionForResource[];
};

type TableAction = {
  id: string;
  name: string;
  action: NextPatchingActionForResource;
};

const columnHelper = createColumnHelper<TableAction>();

const COLUMNS = [
  columnHelper.accessor((row) => row.name, {
    header: "Next Patching Action",
    cell: ({ row }) => {
      const formattedDate = dayjs(row.original.action.startTime).format(
        dateFormat.dayMonthYearHourMinute
      );

      return (
        <NextPatchingActionTooltip
          startTime={row.original.action.startTime}
          scheduleTimezone={row.original.action.scheduleTimezone}
          style={{ zIndex: theme.zindex.topoftheworld }}
        >
          <Link
            to={`${ROUTES.events.generateDetails(
              row.original.action.eventId
            )}#action=${row.original.action.planActionId}`}
          >
            {formattedDate}
          </Link>
        </NextPatchingActionTooltip>
      );
    },
    meta: FlexColumn,
  }),
] as never;

export function NextPatchingActionsModal({ isOpen, onClose, actions }: Props) {
  const tableData = useMemo(
    () =>
      actions.map((action) => ({
        id: action.planActionId,
        name: action.startTime,
        action,
      })),
    [actions]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      contentLabel="Schedules Overlapped"
      css={{ maxWidth: "512px" }}
    >
      <Message
        status="danger"
        image={"danger"}
        title="Overlapping patch schedules detected."
      >
        Overlapping patch schedules detected. This resource is included in
        multiple plans at the same time. Remove duplicates to prevent conflicts.
      </Message>
      <ModalContent
        css={{
          marginTop: theme.spacing.spacing02,
          minHeight: "20vh",
        }}
      >
        <TableWrapper<TableAction>
          loading={false}
          data={tableData}
          columns={COLUMNS}
          rowSelection={{}}
          setRowSelection={() => {}}
          stripped
        />
      </ModalContent>
    </Modal>
  );
}
